import { useEffect, useRef } from 'react'

import { useDecision } from '@optimizely/react-sdk'
import useSWR from 'swr'

import { fetchSubscriptionSettingsOverview } from '@library/api-customer-engagement-experience'
import type {
  SubscriptionSettingsAnalytics,
  SubscriptionSettingsSection,
} from '@library/api-customer-engagement-experience'
import { queueTrackingEvent } from '@library/tracking-integration'

export const RESUB_OPTIMISATION_FLAG = 'tomatoes_ofx_webclient_resub_optimisation'
export const RESUB_OPTIMISATION_SWR_KEY = 'subscription-settings-overview'

type UseSubscriptionSettingsOverviewResult = {
  sections: SubscriptionSettingsSection[] | null
  isLoading: boolean
}

// Gating on isSubscriptionLoaded matters: Subscription.js calls its hooks before its
// loaded-check, and isSubscriptionActive defaults to false on the pre-load empty state,
// so without this clause the fetch would fire for active subscribers too during that window.
export function useSubscriptionSettingsOverview(
  isSubscriptionLoaded: boolean,
  isSubscriptionActive: boolean,
): UseSubscriptionSettingsOverviewResult {
  const [decision, isDecisionReady, hasDecisionTimedOut] = useDecision(RESUB_OPTIMISATION_FLAG)
  const shouldFetch = isSubscriptionLoaded && !isSubscriptionActive && decision.enabled

  // While the decision is still resolving, we don't yet know whether to fetch, so treat
  // this as loading too - otherwise we'd briefly render the legacy screen then pop to the
  // experiment once the decision (and fetch) resolve.
  const isDecisionPending =
    isSubscriptionLoaded && !isSubscriptionActive && !isDecisionReady && !hasDecisionTimedOut

  const { data, error } = useSWR(
    shouldFetch ? RESUB_OPTIMISATION_SWR_KEY : null,
    fetchSubscriptionSettingsOverview,
  )

  const sections = data?.status === 200 && data.data?.sections?.length ? data.data.sections : null
  const isLoading = isDecisionPending || (shouldFetch && data === undefined && !error)
  const pageLoad = data?.status === 200 ? data.pageLoad : undefined

  // SWR revalidates on focus / after mutations, so the ref guard stops the page-load
  // event re-firing on every refetch.
  const hasTrackedPageLoad = useRef(false)
  useEffect(() => {
    if (pageLoad && !hasTrackedPageLoad.current) {
      hasTrackedPageLoad.current = true
      queueTrackingEvent<SubscriptionSettingsAnalytics>(pageLoad)
    }
  }, [pageLoad])

  return { sections, isLoading }
}
